import React, { useEffect, useState } from 'react';
import {
  Box,
  ListItem,
  List,
  Link,
  Typography,
  CardMedia
} from '@mui/material';
import { useTheme } from '@mui/system';
import { useGetDealSummary } from '@hooks/useGetDealSummary';
import { PageDealType, PageDealItemType } from '@types';
import {
  parseInternalLinkText,
  isInternalLinkTextValid
} from '@utils/linkUtils';
import trackUse from '@utils/trackUse';

const LinkType = ({ slug, collageImageSmall, collageImage }: PageDealType) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <ListItem
      component="a"
      href={`/p/${slug}`}
      onClick={() => {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 250);
      }}
      sx={{
        color: '#000',
        display: 'block',
        textAlign: 'center'
      }}
    >
      {collageImageSmall || collageImage ? (
        <CardMedia
          component="img"
          src={(collageImage || collageImageSmall).replace('.png', '.webp')}
          alt="Deal Image"
          role="presentation"
          sx={{
            width: '100%',
            height: { xs: '200px', sm: '300px', md: '300px' },
            maxWidth: { xs: '100%', sm: '500px', lg: '600px' },
            objectFit: 'cover', // Ensures the image maintains aspect ratio
            borderRadius: 2, // Adds slight rounding for better aesthetics
            boxShadow: 2, // Adds subtle shadow
            opacity: isDarkMode ? 0.9 : 1, // Adjusts opacity for dark mode
            transition: 'opacity 0.3s ease-in-out',
            margin: '0 auto'
          }}
        />
      ) : null}
    </ListItem>
  );
};

const RelatedLists = ({
  ASIN,
  showImage = true
}: {
  ASIN: string;
  showImage?: boolean;
}) => {
  const { data: deal } = useGetDealSummary(ASIN);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [randomDeal, setRandomDeal] = useState<PageDealType | null>(null);

  const getPageDealsFromPageDealItems = (
    pageDealItems: PageDealItemType[]
  ): PageDealType[] => {
    return pageDealItems
      .map((pgi) => {
        return {
          ...pgi.PageDeal
        };
      })
      .filter((pd) => pd.collageImage);
  };

  useEffect(() => {
    if (deal && deal.PageDealItems && deal.PageDealItems.length > 0) {
      const pageDeals = getPageDealsFromPageDealItems(deal.PageDealItems);

      // If deal or ASIN changes, select a random PageDeal
      if (
        pageDeals.length > 0 &&
        (!randomDeal || randomDeal.slug !== pageDeals?.[0]?.slug)
      ) {
        const randomIndex = Math.floor(Math.random() * pageDeals.length);
        setRandomDeal(pageDeals[randomIndex]);
      }
    }
  }, [deal, ASIN]);

  if (!deal || !randomDeal) {
    return null;
  }

  const { before, linkText, after } = parseInternalLinkText(
    randomDeal?.internalLinkText
  );
  const showRelatedDealsText = !isInternalLinkTextValid(
    randomDeal?.internalLinkText
  );

  return (
    <Box
      sx={{
        backgroundColor: isDarkMode ? '#282e4c' : '#414970',
        borderRadiusTopLeft: '4px',
        borderRadiusTopRight: '4px',
        padding: showImage ? '0px 30px 20px 30px' : '0 30px',
        marginLeft: '-16px',
        marginRight: '-16px',
        '@media (max-width: 540px)': {
          padding: showImage ? '0px 16px 16px 16px' : '0 16px',
          marginLeft: '-17px !important',
          marginRight: '-17px !important'
        }
      }}
    >
      <Box padding="12px 0">
        <Typography
          sx={{
            color: isDarkMode ? theme.palette.primary.text : '#FFF',
            fontSize: { xs: '14px', sm: '1.15rem' }, // subtitle2 size on mobile, h6 size on larger screens
            lineHeight: { xs: '22px', sm: '28px' }
          }}
          component="div"
          variant="subtitle1"
          textAlign="center"
        >
          {showRelatedDealsText ? (
            'Related Deals'
          ) : (
            <>
              {before}
              {linkText && (
                <Link
                  href={`/p/${randomDeal.slug}`}
                  underline="hover"
                  variant="subtitle1"
                  sx={{
                    color: isDarkMode ? theme.palette.primary.text : '#FFF',
                    fontSize: { xs: '14px', sm: '1.15rem' }, // subtitle2 size on mobile, h6 size on larger screens
                    lineHeight: { xs: '22px', sm: '28px' },
                    // bold
                    fontWeight: 'bold'
                  }}
                  onClick={() => {
                    trackUse({
                      item: 'internal-link-text-click',
                      value: `${randomDeal.slug} - ${window?.location?.href}`,
                      type: 'ACTION'
                    });
                  }}
                >
                  {linkText}
                </Link>
              )}
              {after}
            </>
          )}
        </Typography>
      </Box>
      {showImage ? (
        <Box
          sx={{
            // backgroundColor: '#fff',
            borderRadius: '4px',
            // maxWidth: '400px',
            margin: '0 auto'
          }}
        >
          <List dense>
            <LinkType key={randomDeal.slug} {...randomDeal} />
          </List>
        </Box>
      ) : null}
    </Box>
  );
};

export default RelatedLists;
